// Session helpers for the public /live viewer page. This module OWNS the
// "liveGuestToken" storage scheme — every other reader goes through
// getGuestToken() (or replicates its order and says so).
//
// "Joined" means the browser carries EITHER a real patient-portal session
// (localStorage "token") OR a livestream guest session (localStorage
// "liveGuestToken"). Both ride the SAME `Authorization: Bearer <jwt>` header
// (see relayEnvironment.ts) — the server tells them apart by the JWT's
// `role` claim ("patient" vs "liveGuest"; server/routes/patient-auth-routes.ts).
//
// DURABLE GUEST SESSIONS (owner decision 2026-09-03). The guest token used to
// live in sessionStorage, tab-lifetime only, on the reasoning that a guest
// join is not a durable login. That cost a returning viewer their details on
// every new tab and every browser restart — for a weekly, recurring show, the
// exact friction the "no re-entry" join link exists to remove. It is now a
// durable localStorage token, and the server's liveGuest JWT expiry moved 12h
// → 45d to match (server/services/patientJwtService.ts). The token stays
// scope-limited by its role claim — "liveGuest" buys live-viewer actions only,
// never portal access — which is what makes a long-lived bearer acceptable.
//
// sessionStorage is still READ, but only as a legacy migration path: viewers
// who joined under the old scheme (or mid-tab across the deploy) get their
// token lifted into localStorage on the first read.

// WATCH TOKENS ARE NOT SESSIONS (Group J · J1c). This module also owns the
// "live:watch-token" scheme — the opaque, per-event `liveWatch` JWT the server
// hands an ANONYMOUS watcher (server/services/patientJwtService.ts). It
// authenticates nobody, satisfies no mutation guard, and buys exactly two
// things: being counted once in the public viewer count, and reading a chat
// feed that is already on every other viewer's screen. It rides its OWN header
// (`X-Live-Watch`, see relayEnvironment.ts), never Authorization — so a device
// can hold a session AND a watch token without either clobbering the other.
const GUEST_TOKEN_KEY = "liveGuestToken";

// ONE key holding JSON, not two keys: the token is worthless without the event
// it claims (the server compares the claim against the id being read), and a
// pair of independent slots can be torn — a write that half-succeeds would
// leave a token pointing at the wrong broadcast.
const WATCH_TOKEN_KEY = "live:watch-token";

/**
 * The livestream guest token, if this browser has one. Reads localStorage
 * first (the durable home since 2026-09-03) and falls back to the legacy
 * sessionStorage slot, MIGRATING what it finds there so the viewer keeps the
 * session once this tab closes.
 */
export function getGuestToken(): string | null {
  let durable: string | null = null;
  try {
    durable = window.localStorage.getItem(GUEST_TOKEN_KEY);
  } catch {
    // Storage can throw in locked-down/private-browsing contexts — fall
    // through to the legacy slot rather than crashing the page.
  }
  if (durable) return durable;

  let legacy: string | null = null;
  try {
    legacy = window.sessionStorage.getItem(GUEST_TOKEN_KEY);
  } catch {
    // Same as above — no legacy token reachable, so there is nothing to
    // migrate and the viewer simply isn't joined.
  }
  if (!legacy) return null;

  // Migrate, each step guarded on its own: a copy that throws must not take
  // the token we already read with it, and a failed cleanup must not undo a
  // successful copy. Worst case the token is readable from both slots, which
  // is harmless — they hold the same value.
  try {
    window.localStorage.setItem(GUEST_TOKEN_KEY, legacy);
  } catch {
    // Best-effort — the viewer stays joined for this tab either way.
  }
  try {
    window.sessionStorage.removeItem(GUEST_TOKEN_KEY);
  } catch {
    // Leaving the legacy copy behind is harmless; clearLiveSession() clears
    // both slots anyway.
  }
  return legacy;
}

/**
 * A real patient-portal session (localStorage "token") — the STRONGER of the
 * two lanes, and the only one relayEnvironment.ts will send once it exists.
 *
 * Written for the `?j=` short-circuit in LiveStreamApp: a portal session
 * outranks the guest lane, so exchanging a signup link under it would mint a
 * guest token the Bearer header never reaches for. A merely-present GUEST
 * token must NOT short-circuit that exchange — the exchange is idempotent
 * server-side and storeGuestToken() simply refreshes the stored token, which
 * is exactly what a returning viewer following a fresh link wants.
 */
export function hasPortalSession(): boolean {
  try {
    return !!window.localStorage.getItem("token");
  } catch {
    // Storage can throw in locked-down/private-browsing contexts — treat as
    // "no portal session".
    return false;
  }
}

// DELIBERATELY BLIND TO THE WATCH TOKEN. "Joined" is a promise about what this
// browser can DO — send a message, react, claim a handle — and a watch token
// can do none of it. If this ever learned to count one, an anonymous watcher
// would render the joined shell with a live composer whose every send is
// refused by the server, which is the exact failure the 2026-08-24 recovery
// path exists to prevent.
export function hasLiveSession(): boolean {
  return hasPortalSession() || !!getGuestToken();
}

/** The event a stored watch token was minted for, and the token itself. */
export type StoredWatchToken = { eventId: string; token: string };

/**
 * The watch token this device holds, if any. Null for anything that is not a
 * well-formed `{eventId, token}` pair — a truncated write, a hand-edited value
 * or a shape from a future version is treated as "no token", which costs the
 * visitor a re-mint and nothing else.
 */
export function getWatchToken(): StoredWatchToken | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(WATCH_TOKEN_KEY);
  } catch {
    // Storage can throw in locked-down/private-browsing contexts — the visitor
    // simply watches uncounted rather than crashing the page.
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredWatchToken> | null;
    if (
      !parsed ||
      typeof parsed.eventId !== "string" ||
      typeof parsed.token !== "string" ||
      !parsed.eventId ||
      !parsed.token
    ) {
      return null;
    }
    return { eventId: parsed.eventId, token: parsed.token };
  } catch {
    // Not JSON at all. Same answer as above.
    return null;
  }
}

/**
 * Remember the token so a refresh does NOT mint a second watcher. Every call
 * of `startLiveWatch` draws a fresh `watcherKey` and writes a row, so this
 * write is what keeps one person to one row per broadcast — and with it the
 * public viewer count and the watch-first denominator honest.
 */
export function storeWatchToken(eventId: string, token: string): void {
  try {
    window.localStorage.setItem(
      WATCH_TOKEN_KEY,
      JSON.stringify({ eventId, token }),
    );
  } catch {
    // Best-effort — the row already exists server-side. The cost is that a
    // refresh in this rare storage-blocked case counts the person twice.
  }
}

/**
 * Forget it. NOT called by clearLiveSession (see the comment there) and not by
 * anything on the join path; it exists so the scheme this module owns can be
 * un-owned from one place if a future surface needs to.
 */
export function clearWatchToken(): void {
  try {
    window.localStorage.removeItem(WATCH_TOKEN_KEY);
  } catch {
    // Storage can throw in locked-down contexts, nothing to clear there.
  }
}

export function storeGuestToken(token: string): void {
  try {
    window.localStorage.setItem(GUEST_TOKEN_KEY, token);
  } catch {
    // Best-effort — the join still succeeded server-side; the viewer just
    // won't stay "joined" across a refresh in this rare storage-blocked case.
  }
}

/**
 * Forget the GUEST session and nothing else, the "Not you?" lane on the
 * returning-viewer join sheet (JS3), where a second person on one device says
 * the saved details aren't theirs.
 *
 * BOTH guest slots go, not just the durable one: getGuestToken() migrates a
 * legacy sessionStorage token straight back into localStorage on its next
 * read, so clearing one half would hand the token back on the very next
 * render.
 *
 * DELIBERATELY NARROWER THAN clearLiveSession(). The portal token
 * (localStorage "token") is a different, stronger lane that nothing on this
 * path has any quarrel with, and the watch token is left alone for the reason
 * clearLiveSession() documents at length: clearing it mints a SECOND
 * `LiveAnonymousWatcher` row for the same person on the same broadcast and
 * double-counts them in the public viewer count.
 */
export function clearGuestToken(): void {
  // Independent try/catch per slot, same as clearLiveSession(): a throw
  // clearing one storage must not leave the token behind in the other.
  try {
    window.localStorage.removeItem(GUEST_TOKEN_KEY);
  } catch {
    // Storage can throw in locked-down contexts, nothing to clear there.
  }
  try {
    window.sessionStorage.removeItem(GUEST_TOKEN_KEY);
  } catch {
    // Same as above.
  }
}

// Called when the server rejects the session's JWT (UNAUTHENTICATED — expired
// portal token, deleted patient, forged/stale guest token). This is also the
// recovery path when the guest token eventually expires on its own (now 45d
// out, not 12h). ALL slots must go: relayEnvironment.ts prefers localStorage
// "token" over the guest slot, so a dead portal token left in place would keep
// clobbering any fresh guest token the viewer mints by re-joining, and the chat
// would stay broken forever (owner-hit incident, 2026-08-24 pre-show). A token
// the server has rejected is worthless to every surface, so removing it can't
// log anyone out of anything that still works. The legacy sessionStorage guest
// slot is cleared too — a dead token left there would be migrated straight back
// into localStorage by the next getGuestToken().
//
// THE WATCH TOKEN IS DELIBERATELY LEFT ALONE (Group J · J1c). A dead session is
// not a dead watcher: the two are issued by different mutations, carried on
// different headers and verified by different functions, so nothing the server
// just rejected says anything about the `liveWatch` JWT. Clearing it here would
// make every session expiry mint a SECOND `LiveAnonymousWatcher` row for the
// same person on the same broadcast — double-counting them in the public viewer
// count and inflating the watch-first denominator, which is the one number this
// feature exists to measure.
export function clearLiveSession(): void {
  // Independent try/catch per slot: a throw clearing one storage must not
  // leave the dead token behind in the others.
  try {
    window.localStorage.removeItem(GUEST_TOKEN_KEY);
  } catch {
    // Storage can throw in locked-down contexts, nothing to clear there.
  }
  try {
    window.sessionStorage.removeItem(GUEST_TOKEN_KEY);
  } catch {
    // Same as above.
  }
  try {
    window.localStorage.removeItem("token");
  } catch {
    // Same as above.
  }
}
